import {
  createFileRoute,
  Link,
  type SearchSchemaInput,
} from "@tanstack/react-router";
import FinancialCard from "@/components/new-ui/financial-card";
import { useStore } from "@tanstack/react-store";
import userStore, { isProfileConfirmed } from "@/stores/user-store";
import Buy from "@/components/buy";
import LastTransactions from "@/components/last-transactions";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { check } from "@/queries/login";
import { getProfile } from "@/queries/profile";
import { disableSplash } from "@/lib/utils";
import { setUser } from "@sentry/react";
import { themeStore } from "@/stores/theme-store";
import { CheckIcon, CircleAlert } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ChargeProvider } from "@/stores/charge-store";
import { MultiChargeStoreProvider } from "@/stores/multi-charge-store";

export const Route = createFileRoute("/")({
  validateSearch: (search?: Record<string, unknown> & SearchSchemaInput) => {
    if (search && Object.keys(search).length > 0) {
      return {
        t: typeof search.t === "string" ? search.t : undefined,
        r: typeof search.r === "string" ? search.r : undefined,
        next: search.next,
      };
    }
    return undefined;
  },
  search: {},
  onEnter(ctx) {
    if (!ctx.search) {
      return;
    }

    const { t, r } = ctx.search;
    if (typeof window !== "undefined" && t && r) {
      userStore.setState((prev) => ({
        ...prev,
        token: t,
        refresh_token: r,
        isLoggedIn: true,
      }));
      // Clear query parameters after setting state
      if (window?.history?.replaceState) {
        const url = new URL(window.location.href);
        url.search = "";
        window.history.replaceState({}, document.title, url.toString());
      }
    }
  },
  component: NewDashboard,
});

function NewDashboard() {
  const {
    balance,
    takenValues,
    today_points,
    profit,
    user: currentUser,
  } = useStore(userStore);
  const { chargeLayout } = useStore(themeStore);

  const checkQuery = useQuery({
    queryFn: () => check(),
    queryKey: ["user", "check"],
  });

  const profileQuery = useQuery({
    queryFn: () => getProfile(),
    queryKey: ["get", "profile"],
  });

  console.log(checkQuery.data);
  console.log(profileQuery.data);

  useEffect(() => {
    if (!checkQuery.isFetching) {
      disableSplash();

      const user = checkQuery.data?.data?.user || {};
      setUser({
        username: user.mobile,
        id: user.id,
        email: user.name,
      });
      userStore.setState((prev) => ({
        ...prev,
        user: checkQuery.data?.data?.user || {},
        points: checkQuery.data?.data.user.points || 0,
        setting: checkQuery.data?.data?.setting?.settings || null,
        two_step: checkQuery.data?.data?.user?.two_step || false,
      }));
    }
  }, [checkQuery]);

  useEffect(() => {
    if (profileQuery.data?.data) {
      userStore.setState((prev) => ({
        ...prev,
        profile: profileQuery.data.data.data,
      }));
    }
  }, [profileQuery.data]);

  useEffect(() => {
    const node = document.body.querySelector("#splash-screen");
    if (node) {
      document.body.removeChild(node);
    }
  }, []);

  return (
    <ChargeProvider>
      <MultiChargeStoreProvider>
        {!currentUser?.profile_id ? (
          <>
            <Alert className="col-span-1 md:col-span-4">
              <CircleAlert className="size-5" />
              <AlertTitle>تکمیل پروفایل</AlertTitle>
              <AlertDescription>
                برای تبدیل شدن اکانت خود به پنل پروفایل خود را تکمیل نمایید
              </AlertDescription>
            </Alert>
            <div className="flex flex-col gap-2 justify-center items-center h-full w-full relative col-span-4">
              برای دسترسی به امکانات پنل باید پروفایل خود را تکمیل کنید
              <Link to="/profile/complete">
                <Button>تکمیل پروفایل</Button>
              </Link>
            </div>
          </>
        ) : currentUser?.type === "ordinary" && !isProfileConfirmed(currentUser) ? (
          <Alert className="col-span-1 md:col-span-4">
            <CheckIcon className="size-5" />
            <AlertTitle>در انتظار تایید</AlertTitle>
            <AlertDescription>
              پروفایل شما در دست بررسی می باشد به محض تایید برای شما پیامک
              ارسال خواهد شد
            </AlertDescription>
          </Alert>
        ) : currentUser?.type === "ordinary" || currentUser?.type === "panel" ? (
          <>
            <div className="grid grid-cols-3 lg:grid-cols-4 gap-1 lg:gap-10 mt-5">
              <div className="col-span-3 lg:col-span-1">
                <FinancialCard
                  title="اعتبار شما:"
                  value={balance}
                  hasIncreaseButton={currentUser?.type === "panel"}
                />
              </div>
              <FinancialCard title="دریافتی از مشتری:" value={takenValues} />
              <FinancialCard title="درآمد روزانه:" value={profit} />
              <FinancialCard
                title="امتیاز شما:"
                value={today_points}
                hasRial={false}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-5">
              {currentUser?.type === "panel" && (
                <div className="lg:col-span-6">
                  <Buy isNew={chargeLayout == "new"} />
                </div>
              )}
              <div
                className={
                  currentUser?.type === "panel"
                    ? "lg:col-span-6"
                    : "lg:col-span-12"
                }
              >
                <LastTransactions />
              </div>
            </div>
          </>
        ) : (
          <></>
        )}
      </MultiChargeStoreProvider>
    </ChargeProvider>
  );
}
